import { omit } from "lodash";

import type { Space } from "@/react/types";

import type { UploadedImage } from "../../store";
import type { ImagePostFormPost, ImagePostFormValues } from "../types";

interface FormPayloadParams {
  payload: ImagePostFormValues;
  space: Space;
  uploadedImages: UploadedImage[];
}

interface GalleryImageAttributes {
  height: number;
  id?: number;
  image: string;
  width: number;
}

interface ImagePostPayload {
  post: Omit<ImagePostFormValues, "gallery"> & {
    gallery_attributes: ImagePostFormValues["gallery"] & {
      images_attributes: GalleryImageAttributes[];
    };
    space_id: number;
  };
}

const defaultValues = (post: ImagePostFormPost): ImagePostFormValues => ({
  meta_tag_attributes: post.meta_tag_attributes,
  tiptap_body: post.tiptap_body,
  hide_meta_info: post.hide_meta_info,
  is_comments_disabled: post.is_comments_disabled,
  is_comments_closed: post.is_comments_closed,
  is_liking_disabled: post.is_liking_disabled,
  is_truncation_disabled: post.is_truncation_disabled,
  pin_to_top: post.pin_to_top,
  pinned_at: post.pinned_at,
  hide_from_featured_areas: post.hide_from_featured_areas,
  gallery: {
    downloadable_images: post.gallery?.downloadable_images,
  },
  slug: post.slug,
  user_id: post.user_id,
  status: post.status,
  published_at: post.published_at,
  topics: normalizeTopicIds(post.topics),
});

const formPayload = ({
  payload,
  space,
  uploadedImages,
}: FormPayloadParams): ImagePostPayload => ({
  post: {
    ...omit(payload, ["gallery"]),
    space_id: space.id,
    gallery_attributes: {
      ...(payload.gallery || {}),
      images_attributes: uploadedImages.map(image => ({
        id: image.id,
        image: image.signed_id,
        width: image.width,
        height: image.height,
      })),
    },
  },
});

const parsePublishedAt = (value: unknown): Date | undefined => {
  if (!value) {
    return undefined;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  if (typeof value !== "string" && typeof value !== "number") {
    return undefined;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? undefined : date;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isTopicRecord = (topic: unknown): topic is { id: number } =>
  isRecord(topic) && typeof topic.id === "number";

const normalizeTopicIds = (topics: unknown): number[] => {
  if (!Array.isArray(topics)) {
    return [];
  }

  return topics.flatMap(topic => {
    if (typeof topic === "number") {
      return [topic];
    }

    return isTopicRecord(topic) ? [topic.id] : [];
  });
};

const isImagePostFormPost = (post: unknown): post is ImagePostFormPost =>
  isRecord(post) && "id" in post && "gallery" in post;

const getImagePostFormPost = (post: unknown) =>
  isImagePostFormPost(post) ? post : undefined;

const requireImagePostFormPost = (post: unknown) => {
  const imagePost = getImagePostFormPost(post);

  if (!imagePost) {
    throw new Error("Image post not found");
  }

  return imagePost;
};

export {
  defaultValues,
  formPayload,
  getImagePostFormPost,
  normalizeTopicIds,
  parsePublishedAt,
  requireImagePostFormPost,
};
